import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import type { PlanningPeriod, Student, Assignment, Vacation, Department, SchoolClass, Practice, WishReportBlock, WishReportEntry } from "../types";
import { vacationAppliesTo } from "./vacations";
import { formatDateTime, isoWeek } from "./date-utils";

// ── Shared private helpers ────────────────────────────────────────────────────

async function loadLogo(): Promise<{ dataUrl: string; w: number; h: number } | null> {
  try {
    const resp = await fetch("/Logo.png");
    const blob = await resp.blob();
    const origDataUrl = await new Promise<string>(resolve => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => resolve("");
      reader.readAsDataURL(blob);
    });
    if (!origDataUrl) return null;
    return await new Promise<{ dataUrl: string; w: number; h: number } | null>(resolve => {
      const img = new Image();
      img.onload = () => {
        const maxPx = 300;
        const scale = Math.min(maxPx / img.naturalWidth, maxPx / img.naturalHeight, 1);
        const cw = Math.round(img.naturalWidth * scale);
        const ch = Math.round(img.naturalHeight * scale);
        const canvas = document.createElement("canvas");
        canvas.width = cw; canvas.height = ch;
        canvas.getContext("2d")!.drawImage(img, 0, 0, cw, ch);
        resolve({ dataUrl: canvas.toDataURL("image/jpeg", 0.85), w: cw, h: ch });
      };
      img.onerror = () => resolve(null);
      img.src = origDataUrl;
    });
  } catch { return null; }
}

/** Adds logo + two-line header to current PDF page. Returns next Y position. */
function addLogoAndHeader(
  doc: jsPDF,
  logo: { dataUrl: string; w: number; h: number } | null,
  title: string,
  subtitle: string,
): number {
  let y = 14;
  if (logo) {
    const displayW = 30;
    const displayH = (logo.h / logo.w) * displayW;
    doc.addImage(logo.dataUrl, "JPEG", 14, y, displayW, displayH);
    y += displayH + 4;
  }
  doc.setFontSize(13); doc.setFont("helvetica", "bold"); doc.setTextColor(30, 30, 30);
  doc.text(title, 14, y); y += 7;
  doc.setFontSize(9); doc.setFont("helvetica", "normal"); doc.setTextColor(100, 100, 100);
  doc.text(subtitle, 14, y); y += 6;
  return y;
}

function formatDE(d: Date): string {
  return d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
}

function formatDEShort(d: Date): string {
  return d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" });
}

function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace("#", "").padEnd(6, "0");
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}

/** Returns the start date of a period-relative week (0-indexed). */
function weekStartDate(periodStart: string, weekOffset: number): Date {
  const d = new Date(periodStart);
  d.setDate(d.getDate() + weekOffset * 7);
  return d;
}

function getISOWeek(d: Date): number {
  const dt = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  dt.setUTCDate(dt.getUTCDate() + 4 - (dt.getUTCDay() || 7));
  const y = new Date(Date.UTC(dt.getUTCFullYear(), 0, 1));
  return Math.ceil(((dt.getTime() - y.getTime()) / 86400000 + 1) / 7);
}

function safeFilename(name: string, ext: string): string {
  return `${name}.${ext}`.replace(/[^\w\säöüÄÖÜß.\-]/g, "_");
}

/** Wie die Schulverwaltung: Nachname, dann Vorname. Unbekannte Schüler ans Ende. */
function byLastFirst(a: Student | undefined, b: Student | undefined): number {
  if (!a || !b) return (a ? 0 : 1) - (b ? 0 : 1);
  return (a.lastName || "").localeCompare(b.lastName || "", "de")
    || (a.firstName || "").localeCompare(b.firstName || "", "de");
}

/** Einsätze nach Schüler (Nachname, Vorname), je Schüler nach Startwoche. Die gruppierten
 *  Downloads behalten diese Reihenfolge innerhalb jeder Gruppe. */
function assignmentsByStudent(assignments: Assignment[], students: Student[]): Assignment[] {
  const byId = new Map(students.map(s => [+s.id, s]));
  return [...assignments].sort((a, b) =>
    byLastFirst(byId.get(+a.studentId), byId.get(+b.studentId)) || a.startWeek - b.startWeek);
}

// ── Practices Export (extrahiert aus practices.ts) ────────────────────────────

export interface PracticeExportData {
  practice: { name: string; plz?: string; ort?: string; street?: string; houseNo?: string };
  periodName: string;
  assignments: Array<{
    studentName: string;
    className: string;
    departmentShortName: string;
    periodStartDate: string;
    startWeek: number;
    durationWeeks: number;
  }>;
}

export async function exportPracticeAssignments(fmt: "xlsx" | "pdf", data: PracticeExportData): Promise<void> {
  const { practice, periodName, assignments } = data;
  const filename = safeFilename(`Planung_${practice.name}_${periodName}`, fmt);

  function buildRow(a: typeof assignments[0]): string[] {
    const from = new Date(a.periodStartDate);
    from.setDate(from.getDate() + a.startWeek * 7);
    const to = new Date(a.periodStartDate);
    to.setDate(to.getDate() + (a.startWeek + a.durationWeeks) * 7 - 1);
    const s = from.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" });
    const e = to.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" });
    return [a.studentName || "–", a.className || "–", a.departmentShortName || "–", s === e ? s : `${s} – ${e}`];
  }

  if (fmt === "xlsx") {
    const rows = [["Schüler", "Klasse", "Fachbereich", "Zeitraum"], ...assignments.map(buildRow)];
    const ws = XLSX.utils.aoa_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Planung");
    XLSX.writeFile(wb, filename);
  } else {
    const logo = await loadLogo();
    const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const y = addLogoAndHeader(doc, logo, practice.name, `Zeitraum: ${periodName}   ·   Stand: ${new Date().toLocaleDateString("de-DE")}`);
    autoTable(doc, {
      startY: y,
      head: [["Schüler", "Klasse", "Fachbereich", "Zeitraum"]],
      body: assignments.map(buildRow),
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: [37, 99, 235] as [number, number, number], textColor: 255, fontStyle: "bold" },
      alternateRowStyles: { fillColor: [245, 247, 255] as [number, number, number] },
      margin: { left: 14, right: 14 },
    });
    doc.save(filename);
  }
}

// ── Planning Exports ──────────────────────────────────────────────────────────

export interface PlanningExportData {
  period: PlanningPeriod;
  students: Student[];
  assignments: Assignment[];
  vacations: Vacation[];
  departments: Department[];
  classes: SchoolClass[];
  practices: Practice[];
  /** Ob das Abrechnung-Feature aktiv ist (steuert die "Abgerechnet"-Spalte im Export). */
  abrechnungEnabled?: boolean;
}

/** "Abgerechnet"-Zellwert für die XLSX-Exporte. */
function abgerechnetLabel(a: Assignment): string {
  return a.abgerechnet ? "Ja" : "Nein";
}

/** Straße mit Hausnummer, PLZ und Ort einer Praktikumsstelle, jeweils leer, wenn nicht gepflegt. */
function practiceAddressParts(p: Practice): { street: string; plz: string; ort: string } {
  const street = p.street && p.houseNo ? `${p.street} ${p.houseNo}` : (p.street || "");
  return { street, plz: p.plz || "", ort: p.ort || "" };
}

function practiceAddressLine(p: Practice): string {
  const { street, plz, ort } = practiceAddressParts(p);
  return [street, plz, ort].filter(Boolean).join(", ");
}

/**
 * Welche Kontaktdaten der Praktikumsstelle der Download „Nach Schülern“ mitnimmt.
 * Der Name der Stelle steht ohnehin drin. Alles stammt aus dem Bereich
 * „Informationen für Schüler“, weil das PDF an die Schüler ausgegeben wird.
 */
export interface SchuelerContactOptions {
  address: boolean;
  person: boolean;
  phone: boolean;
  email: boolean;
  comment: boolean;
}

/**
 * Welche Stammdaten der Schüler der Download „Nach Prakt.stellen“ mitnimmt.
 * Name und Klasse stehen ohnehin drin.
 */
export interface StudentMasterDataOptions {
  address: boolean;
  phone: boolean;
  email: boolean;
}

/** Straße mit Hausnummer, PLZ und Ort eines Schülers, jeweils leer, wenn nicht gepflegt. */
function studentAddressParts(s: Student): { street: string; plz: string; ort: string } {
  const street = s.street && s.houseNo ? `${s.street} ${s.houseNo}` : (s.street || "");
  return { street, plz: s.plz || "", ort: s.ort || "" };
}

interface WeekRow { index: number; kw: number; from: Date; to: Date; label: string; }

function buildWeekRows(period: PlanningPeriod): WeekRow[] {
  const start = new Date(period.startDate);
  const end = new Date(period.endDate);
  const totalWeeks = Math.ceil((end.getTime() - start.getTime()) / (7 * 24 * 3600 * 1000)) + 1;
  const rows: WeekRow[] = [];
  for (let i = 0; i < totalWeeks; i++) {
    const from = weekStartDate(period.startDate, i);
    const to = weekStartDate(period.startDate, i + 1);
    to.setDate(to.getDate() - 1);
    rows.push({ index: i, kw: getISOWeek(from), from, to, label: `KW ${getISOWeek(from)} · ${formatDEShort(from)}–${formatDEShort(to)}` });
  }
  return rows;
}

// ── 1. Gesamtansicht (Matrix) ─────────────────────────────────────────────────

export async function exportGesamt(fmt: "xlsx" | "pdf", data: PlanningExportData): Promise<void> {
  const { period, students, assignments, vacations, departments } = data;
  const filename = safeFilename(`Planung_Gesamt_${period.name}`, fmt);
  const weekRows = buildWeekRows(period);

  function getCellInfo(studentId: number, weekIndex: number): { text: string; color?: string } {
    const vac = vacations.find(v =>
      vacationAppliesTo(v, studentId) && v.startWeek <= weekIndex && v.endWeek >= weekIndex
    );
    if (vac) return { text: `FERIEN: ${vac.name}`, color: "#CCE5FF" };

    const a = assignments.find(a =>
      a.studentId === studentId && a.startWeek <= weekIndex && a.startWeek + a.durationWeeks > weekIndex
    );
    if (!a) return { text: "" };

    const dept = departments.find(d => d.id === a.department);
    const label = a.isFreeInput
      ? (a.freeInputText || dept?.shortName || a.department)
      : `${dept?.shortName || a.department}${a.practiceName ? ` – ${a.practiceName}` : ""}`;
    return { text: label, color: dept?.color };
  }

  if (fmt === "xlsx") {
    const header = ["KW / Datum", ...students.map(s => `${s.firstName} ${s.lastName}`)];
    const rows: any[][] = [header];
    weekRows.forEach(week => {
      const row: any[] = [week.label];
      students.forEach(s => row.push(getCellInfo(s.id, week.index).text));
      rows.push(row);
    });

    // Merged cells for multi-week assignments and vacations
    const merges: XLSX.Range[] = [];
    students.forEach((s, colIdx) => {
      assignments.filter(a => a.studentId === s.id && a.durationWeeks > 1).forEach(a => {
        const sr = a.startWeek + 1; // +1 for header row
        const er = a.startWeek + a.durationWeeks; // inclusive
        if (er < rows.length) merges.push({ s: { r: sr, c: colIdx + 1 }, e: { r: er, c: colIdx + 1 } });
      });
      vacations.forEach(v => {
        if (!vacationAppliesTo(v, s.id)) return;
        const dur = v.endWeek - v.startWeek;
        if (dur > 0) {
          const sr = v.startWeek + 1;
          const er = v.endWeek + 1;
          if (er < rows.length) merges.push({ s: { r: sr, c: colIdx + 1 }, e: { r: er, c: colIdx + 1 } });
        }
      });
    });

    const ws = XLSX.utils.aoa_to_sheet(rows);
    if (merges.length) ws["!merges"] = merges;
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Gesamtplan");
    XLSX.writeFile(wb, filename);

  } else {
    const logo = await loadLogo();
    const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
    const dateLabel = `${formatDE(new Date(period.startDate))} – ${formatDE(new Date(period.endDate))}`;
    const subtitle = `${dateLabel}   ·   Stand: ${new Date().toLocaleDateString("de-DE")}`;
    const PAGE_STUDENTS = 8;

    for (let chunkStart = 0; chunkStart < students.length; chunkStart += PAGE_STUDENTS) {
      const chunk = students.slice(chunkStart, chunkStart + PAGE_STUDENTS);
      if (chunkStart > 0) doc.addPage();

      const y = addLogoAndHeader(doc, logo, `Gesamtplan: ${period.name}`, subtitle);
      const head = [["KW / Datum", ...chunk.map(s => `${s.firstName} ${s.lastName}`)]];
      const body = weekRows.map(week => [
        week.label,
        ...chunk.map(s => getCellInfo(s.id, week.index).text),
      ]);

      autoTable(doc, {
        startY: y,
        head,
        body,
        styles: { fontSize: 7, cellPadding: 2, overflow: "ellipsize" },
        headStyles: { fillColor: [37, 99, 235] as [number, number, number], textColor: 255, fontStyle: "bold" },
        columnStyles: { 0: { cellWidth: 28, fontStyle: "bold" } },
        margin: { left: 10, right: 10 },
        willDrawCell: (hookData) => {
          if (hookData.section !== "body" || hookData.column.index === 0) return;
          const s = chunk[hookData.column.index - 1];
          if (!s) return;
          const { color } = getCellInfo(s.id, hookData.row.index);
          if (color) {
            const [r, g, b] = hexToRgb(color);
            (hookData.cell.styles as any).fillColor = [r, g, b];
            (hookData.cell.styles as any).textColor = [30, 30, 30];
          }
        },
      });
    }
    doc.save(filename);
  }
}

/**
 * Tabelle mit Überschrift unter einer Planung. Der Block wird nicht zerteilt:
 * passt er nicht mehr auf die Seite, beginnt er auf einer neuen. Nur wenn er
 * allein länger ist als eine Seite, läuft er weiter, dann aber nie mitten in
 * einer Zeile. Folgeseiten bekommen oben `pageLabel`, damit das Blatt beim
 * Austeilen zuzuordnen bleibt; die Seite `headerPage` hat ihren Kopf schon.
 */
function renderKeepTogetherBlock(doc: jsPDF, block: {
  heading: string; head: string[]; body: string[][]; startY: number; headerPage: number; pageLabel: string;
}): void {
  const { heading, head, body, startY, headerPage, pageLabel } = block;
  const pageH = doc.internal.pageSize.getHeight();
  const MARGIN_TOP = 18, MARGIN_BOTTOM = 14, HEADING_GAP = 4;
  const columnStyles: Record<number, { cellWidth: number }> = head.length === 3
    ? { 0: { cellWidth: 60 }, 1: { cellWidth: 60 } }
    : {};
  const tableOpts = {
    head: [head],
    body,
    styles: { fontSize: 9, cellPadding: 3, valign: "top" as const },
    headStyles: { fillColor: [37, 99, 235] as [number, number, number], textColor: 255, fontStyle: "bold" as const },
    alternateRowStyles: { fillColor: [245, 247, 255] as [number, number, number] },
    columnStyles,
    rowPageBreak: "avoid" as const,
    margin: { left: 14, right: 14, top: MARGIN_TOP, bottom: MARGIN_BOTTOM },
  };

  // Höhe auf einem Wegwerf-PDF messen. Läuft die Tabelle dort schon über eine
  // Seite hinaus, passt sie nirgends am Stück.
  const probe = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  autoTable(probe, { ...tableOpts, startY: MARGIN_TOP });
  const height = probe.getNumberOfPages() > 1
    ? Infinity
    : (probe as any).lastAutoTable.finalY - MARGIN_TOP;

  const labelled = new Set<number>([headerPage]);
  const labelPage = () => {
    const page = doc.getCurrentPageInfo().pageNumber;
    if (labelled.has(page)) return;
    labelled.add(page);
    doc.setFontSize(8); doc.setFont("helvetica", "normal"); doc.setTextColor(120, 120, 120);
    doc.text(pageLabel, 14, 11);
  };

  let y = startY;
  if (y + HEADING_GAP + height > pageH - MARGIN_BOTTOM) {
    doc.addPage();
    labelPage();
    y = MARGIN_TOP + 2;
  }
  doc.setFontSize(10); doc.setFont("helvetica", "bold"); doc.setTextColor(30, 30, 30);
  doc.text(heading, 14, y);
  autoTable(doc, {
    ...tableOpts,
    startY: y + HEADING_GAP,
    didDrawPage: labelPage,
  });
}

// ── 2. Sortiert nach Schülern ─────────────────────────────────────────────────

export async function exportSchueler(fmt: "xlsx" | "pdf", data: PlanningExportData, contacts?: SchuelerContactOptions): Promise<void> {
  const { period, students, assignments, departments, classes, practices } = data;
  const filename = safeFilename(`Planung_Schueler_${period.name}`, fmt);

  const sorted = [...students].sort(byLastFirst);

  function getStudentAssignments(studentId: number): Assignment[] {
    return assignments.filter(a => a.studentId === studentId).sort((a, b) => a.startWeek - b.startWeek);
  }

  /** Stelle eines Einsatzes; IDs per + vergleichen, pg liefert BIGINT als String. */
  function practiceOf(a: Assignment): Practice | undefined {
    if (a.isFreeInput || !a.practiceId) return undefined;
    return practices.find(p => +p.id === +a.practiceId!);
  }

  function buildAssignmentRow(a: Assignment): string[] {
    const dept = departments.find(d => d.id === a.department);
    const from = weekStartDate(period.startDate, a.startWeek);
    const to = weekStartDate(period.startDate, a.startWeek + a.durationWeeks);
    to.setDate(to.getDate() - 1);
    const practiceName = a.isFreeInput ? (a.freeInputText || "–") : (a.practiceName || "–");
    return [
      dept?.nameDe || a.department,
      practiceName,
      `${formatDE(from)} – ${formatDE(to)}`,
      `${a.durationWeeks} Wo.`,
      `${a.durationWeeks * a.hoursPerWeek} Std.`,
    ];
  }

  const colHeaders = ["Fachbereich", "Praktikumsstelle", "Zeitraum", "Dauer", "Gesamt"];

  if (fmt === "xlsx") {
    const showAbrechnung = !!data.abrechnungEnabled;
    const header = ["Schüler", "Klasse", "Fachbereich", "Praktikumsstelle", "Von", "Bis", "Dauer (Wo.)", "Std./Wo.", "Gesamt-Std.", "Interner Kommentar", "Kommentar für Schüler"];
    if (showAbrechnung) header.push("Abgerechnet");

    // Kontaktspalten hinten anhängen, damit die bisherigen Spalten an ihrem Platz bleiben.
    // „Kommentar Praktikumsstelle“, weil „Kommentar für Schüler“ schon der Einsatz-Kommentar ist.
    const contactCols: [string, (p: Practice) => string][] = [];
    if (contacts?.address) {
      contactCols.push(
        ["Straße", p => practiceAddressParts(p).street],
        ["PLZ", p => practiceAddressParts(p).plz],
        ["Ort", p => practiceAddressParts(p).ort],
      );
    }
    if (contacts?.person) contactCols.push(["Kontaktperson", p => p.contactPersonStudents || ""]);
    if (contacts?.phone) contactCols.push(["Telefon", p => p.phoneStudents || ""]);
    if (contacts?.email) contactCols.push(["E-Mail", p => p.emailStudents || ""]);
    if (contacts?.comment) contactCols.push(["Kommentar Praktikumsstelle", p => p.commentStudents || ""]);
    header.push(...contactCols.map(([label]) => label));

    const xlsRows: string[][] = [header];
    for (const s of sorted) {
      const cls = classes.find(c => c.id === s.classId)?.name || s.className || "–";
      const sAssignments = getStudentAssignments(s.id);
      if (sAssignments.length === 0) {
        xlsRows.push([`${s.firstName} ${s.lastName}`, cls, ...Array(header.length - 2).fill("")]);
      } else {
        for (const a of sAssignments) {
          const dept = departments.find(d => d.id === a.department);
          const from = weekStartDate(period.startDate, a.startWeek);
          const to = weekStartDate(period.startDate, a.startWeek + a.durationWeeks);
          to.setDate(to.getDate() - 1);
          const row = [
            `${s.firstName} ${s.lastName}`, cls,
            dept?.nameDe || a.department,
            a.isFreeInput ? (a.freeInputText || "–") : (a.practiceName || "–"),
            formatDE(from), formatDE(to),
            String(a.durationWeeks), String(a.hoursPerWeek),
            String(a.durationWeeks * a.hoursPerWeek),
            a.internalNote || "", a.studentNote || "",
          ];
          if (showAbrechnung) row.push(abgerechnetLabel(a));
          const p = practiceOf(a);
          row.push(...contactCols.map(([, value]) => (p ? value(p) : "")));
          xlsRows.push(row);
        }
      }
      xlsRows.push([]); // blank row between students
    }
    const ws = XLSX.utils.aoa_to_sheet(xlsRows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Nach Schülern");
    XLSX.writeFile(wb, filename);

  } else {
    const logo = await loadLogo();
    const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const pageH = doc.internal.pageSize.getHeight();

    /** Kontaktdaten der Stellen unter der Planung, je Stelle eine Zeile. */
    function renderContactBlock(sAssignments: Assignment[], startY: number, headerPage: number, studentLabel: string): void {
      const opts = contacts!;
      // Jede Stelle nur einmal, in der Reihenfolge ihres ersten Einsatzes. Der Name
      // kommt vom Einsatz, damit er zur Planungstabelle darüber passt.
      const seen = new Map<string, { name: string; practice?: Practice }>();
      for (const a of sAssignments) {
        if (a.isFreeInput || !a.practiceId) continue;
        const key = String(a.practiceId);
        if (seen.has(key)) continue;
        const practice = practiceOf(a);
        seen.set(key, { name: a.practiceName || practice?.name || "–", practice });
      }
      if (seen.size === 0) return;

      const withContact = opts.person || opts.phone || opts.email;
      const head = ["Praktikumsstelle"];
      if (withContact) head.push("Kontakt");
      if (opts.comment) head.push("Kommentar");

      const body = [...seen.values()].map(({ name, practice: p }) => {
        const first = [name];
        if (opts.address && p) {
          const address = practiceAddressLine(p);
          if (address) first.push(address);
        }
        const row = [first.join("\n")];
        if (withContact) {
          const lines: string[] = [];
          if (opts.person && p?.contactPersonStudents) lines.push(p.contactPersonStudents);
          if (opts.phone && p?.phoneStudents) lines.push(`Tel. ${p.phoneStudents}`);
          if (opts.email && p?.emailStudents) lines.push(p.emailStudents);
          row.push(lines.join("\n"));
        }
        if (opts.comment) row.push(p?.commentStudents || "");
        return row;
      });

      renderKeepTogetherBlock(doc, {
        heading: "Kontaktdaten der Praktikumsstellen", head, body, startY, headerPage, pageLabel: studentLabel,
      });
    }

    let firstStudent = true;

    for (const s of sorted) {
      const cls = classes.find(c => c.id === s.classId)?.name || s.className || "–";
      const sAssignments = getStudentAssignments(s.id);
      if (!firstStudent) doc.addPage();
      firstStudent = false;

      const y = addLogoAndHeader(
        doc, logo,
        `${s.firstName} ${s.lastName} – ${cls}`,
        `Zeitraum: ${period.name}   ·   Stand: ${new Date().toLocaleDateString("de-DE")}`,
      );
      const headerPage = doc.getCurrentPageInfo().pageNumber;

      if (sAssignments.length === 0) {
        doc.setFontSize(9); doc.setTextColor(150, 150, 150);
        doc.text("Keine Zuweisungen in diesem Zeitraum.", 14, y + 6);
        continue;
      }

      autoTable(doc, {
        startY: y,
        head: [colHeaders],
        body: sAssignments.map(buildAssignmentRow),
        styles: { fontSize: 9, cellPadding: 3 },
        headStyles: { fillColor: [37, 99, 235] as [number, number, number], textColor: 255, fontStyle: "bold" },
        alternateRowStyles: { fillColor: [245, 247, 255] as [number, number, number] },
        // Unten 20 statt der üblichen gut 14 mm: dort passt die Stundenzeile immer noch
        // hin und landet nicht allein auf einer neuen Seite.
        margin: { left: 14, right: 14, bottom: 20 },
        willDrawCell: (hookData) => {
          if (hookData.section !== "body" || hookData.column.index !== 0) return;
          const a = sAssignments[hookData.row.index];
          if (!a) return;
          const dept = departments.find(d => d.id === a.department);
          if (dept?.color) {
            const [r, g, b] = hexToRgb(dept.color);
            (hookData.cell.styles as any).fillColor = [r, g, b];
            (hookData.cell.styles as any).textColor = [30, 30, 30];
          }
        },
      });

      // Stundenbilanz
      const hoursByDept = new Map<string, number>();
      for (const a of sAssignments) {
        hoursByDept.set(a.department, (hoursByDept.get(a.department) || 0) + Number(a.durationWeeks) * Number(a.hoursPerWeek));
      }
      let finalY = (doc as any).lastAutoTable.finalY + 5;
      if (finalY > pageH - 12) { doc.addPage(); finalY = 14; }
      doc.setFontSize(8); doc.setFont("helvetica", "normal"); doc.setTextColor(80, 80, 80);
      const parts = [...hoursByDept.entries()].map(([dId, h]) => {
        const dept = departments.find(d => d.id === dId);
        return `${dept?.shortName || dId}: ${h} Std.`;
      });
      doc.text(`Stunden: ${parts.join("  ·  ")}`, 14, finalY);

      if (contacts) renderContactBlock(sAssignments, finalY + 10, headerPage, `${s.firstName} ${s.lastName} · ${cls}`);
    }
    doc.save(filename);
  }
}

// ── 3. Sortiert nach Praktikumsstellen ───────────────────────────────────────

export async function exportPraktikumsstellen(fmt: "xlsx" | "pdf", data: PlanningExportData, stammdaten?: StudentMasterDataOptions): Promise<void> {
  const { period, students, assignments, departments, classes, practices } = data;
  const filename = safeFilename(`Planung_Praktikumsstellen_${period.name}`, fmt);

  const showAbrechnung = !!data.abrechnungEnabled;

  // Group assignments by practice (skip Freie Eingabe).
  // `rows` = 6-spaltige Basis (auch für den PDF-Zweig), `extras` = parallele
  // Zusatzspalten (Kommentare + optional Abgerechnet) nur für den XLSX-Zweig.
  // `students` parallel zu `rows`, für die Stammdaten.
  type PracticeEntry = { name: string; ort: string; address: string; rows: string[][]; extras: string[][]; students: (Student | undefined)[] };
  const practiceMap = new Map<number, PracticeEntry>();

  for (const a of assignmentsByStudent(assignments, students)) {
    if (a.isFreeInput || !a.practiceId) continue;
    // IDs per + vergleichen, pg liefert BIGINT als String
    const pInfo = practices.find(p => +p.id === +a.practiceId!);
    const pName = a.practiceName || pInfo?.name || "–";
    const ort = pInfo?.ort || "";
    const address = pInfo ? practiceAddressLine(pInfo) : "";

    if (!practiceMap.has(a.practiceId)) {
      practiceMap.set(a.practiceId, { name: pName, ort, address, rows: [], extras: [], students: [] });
    }

    const student = students.find(s => +s.id === +a.studentId);
    const studentName = a.studentName || (student ? `${student.firstName} ${student.lastName}` : "–");
    const cls = student ? (classes.find(c => c.id === student.classId)?.name || student.className || "–") : "–";
    const dept = departments.find(d => d.id === a.department);
    const from = weekStartDate(period.startDate, a.startWeek);
    const to = weekStartDate(period.startDate, a.startWeek + a.durationWeeks);
    to.setDate(to.getDate() - 1);

    practiceMap.get(a.practiceId)!.rows.push([
      studentName, cls, dept?.nameDe || a.department, formatDE(from), formatDE(to), `${a.durationWeeks} Wo.`,
    ]);
    const extra = [a.internalNote || "", a.studentNote || ""];
    if (showAbrechnung) extra.push(abgerechnetLabel(a));
    practiceMap.get(a.practiceId)!.extras.push(extra);
    practiceMap.get(a.practiceId)!.students.push(student);
  }

  const sortedPractices = [...practiceMap.entries()].sort((a, b) =>
    a[1].name.localeCompare(b[1].name, "de")
  );

  if (fmt === "xlsx") {
    const header = ["Praktikumsstelle", "Ort", "Fachbereich", "Schüler", "Klasse", "Von", "Bis", "Dauer (Wo.)", "Interner Kommentar", "Kommentar für Schüler"];
    if (showAbrechnung) header.push("Abgerechnet");

    // Stammdaten hinten anhängen, damit die bisherigen Spalten an ihrem Platz bleiben.
    // „(Schüler)“, weil „Ort“ schon der Ort der Stelle ist.
    const masterCols: [string, (s: Student) => string][] = [];
    if (stammdaten?.address) {
      masterCols.push(
        ["Straße (Schüler)", s => studentAddressParts(s).street],
        ["PLZ (Schüler)", s => studentAddressParts(s).plz],
        ["Ort (Schüler)", s => studentAddressParts(s).ort],
      );
    }
    if (stammdaten?.phone) masterCols.push(["Telefon (Schüler)", s => s.phone || ""]);
    if (stammdaten?.email) masterCols.push(["E-Mail (Schüler)", s => s.email || ""]);
    header.push(...masterCols.map(([label]) => label));

    const xlsRows: string[][] = [header];
    let lastId: number | null = null;
    for (const [id, { name, ort, rows, extras, students: rowStudents }] of sortedPractices) {
      if (lastId !== null) xlsRows.push([]); // blank row between practices
      rows.forEach((row, i) => {
        // row = [studentName, cls, deptName, from, to, duration]
        const st = rowStudents[i];
        xlsRows.push([
          name, ort, row[2], row[0], row[1], row[3], row[4], row[5], ...extras[i],
          ...masterCols.map(([, value]) => (st ? value(st) : "")),
        ]);
      });
      lastId = id;
    }
    const ws = XLSX.utils.aoa_to_sheet(xlsRows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Nach Praktikumsstellen");
    XLSX.writeFile(wb, filename);

  } else {
    const logo = await loadLogo();
    const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    let first = true;

    for (const [, { name, address, rows, students: rowStudents }] of sortedPractices) {
      if (!first) doc.addPage();
      first = false;

      const subtitle = [address, `Zeitraum: ${period.name}`, `Stand: ${new Date().toLocaleDateString("de-DE")}`].filter(Boolean).join("   ·   ");
      const y = addLogoAndHeader(doc, logo, name, subtitle);
      const headerPage = doc.getCurrentPageInfo().pageNumber;

      if (rows.length === 0) {
        doc.setFontSize(9); doc.setTextColor(150, 150, 150);
        doc.text("Keine Zuweisungen in diesem Zeitraum.", 14, y + 6);
        continue;
      }

      autoTable(doc, {
        startY: y,
        head: [["Schüler", "Klasse", "Fachbereich", "Von", "Bis", "Dauer"]],
        body: rows,
        styles: { fontSize: 9, cellPadding: 3 },
        headStyles: { fillColor: [37, 99, 235] as [number, number, number], textColor: 255, fontStyle: "bold" },
        alternateRowStyles: { fillColor: [245, 247, 255] as [number, number, number] },
        margin: { left: 14, right: 14 },
      });

      if (stammdaten) renderStudentMasterBlock(doc, rows, rowStudents, (doc as any).lastAutoTable.finalY + 10, headerPage, name);
    }
    doc.save(filename);
  }

  /** Stammdaten der Schüler unter der Planung einer Stelle, jeder Schüler einmal. */
  function renderStudentMasterBlock(doc: jsPDF, rows: string[][], rowStudents: (Student | undefined)[], startY: number, headerPage: number, practiceName: string): void {
    const opts = stammdaten!;
    // Reihenfolge des ersten Einsatzes, Name wie in der Planungstabelle darüber.
    const seen = new Map<string, { name: string; student?: Student }>();
    rows.forEach((row, i) => {
      const st = rowStudents[i];
      const key = st ? String(st.id) : `name:${row[0]}`;
      if (!seen.has(key)) seen.set(key, { name: row[0], student: st });
    });
    if (seen.size === 0) return;

    const withContact = opts.phone || opts.email;
    const head = ["Schüler"];
    if (opts.address) head.push("Adresse");
    if (withContact) head.push("Kontakt");

    const body = [...seen.values()].map(({ name, student: st }) => {
      const row = [name];
      if (opts.address) {
        const { street, plz, ort } = st ? studentAddressParts(st) : { street: "", plz: "", ort: "" };
        row.push([street, [plz, ort].filter(Boolean).join(" ")].filter(Boolean).join("\n"));
      }
      if (withContact) {
        const lines: string[] = [];
        if (opts.phone && st?.phone) lines.push(`Tel. ${st.phone}`);
        if (opts.email && st?.email) lines.push(st.email);
        row.push(lines.join("\n"));
      }
      return row;
    });

    renderKeepTogetherBlock(doc, {
      heading: "Stammdaten der Schüler", head, body, startY, headerPage, pageLabel: practiceName,
    });
  }
}

// ── 4. Nach Kreisverband (DRK) ─────────────────────────────────────────────────

export async function exportKreisverband(fmt: "xlsx" | "pdf", data: PlanningExportData): Promise<void> {
  const { period, students, assignments, departments, classes, practices } = data;
  const filename = safeFilename(`Planung_Kreisverband_${period.name}`, fmt);

  const showAbrechnung = !!data.abrechnungEnabled;
  const OHNE = "Ohne Kreisverband";
  // `rows` enthalten die 7 Basis-Spalten plus die Zusatzspalten (Kommentare +
  // optional Abgerechnet) am Ende. Der PDF-Zweig schneidet die Zusatzspalten via
  // slice(0, 7) wieder ab, die Reihenfolge der Zeilen bleibt dabei gleich.
  type KvEntry = { rows: string[][] };
  const kvMap = new Map<string, KvEntry>();

  for (const a of assignmentsByStudent(assignments, students)) {
    const student = students.find(s => s.id === a.studentId);
    const kv = student?.drkKreisverband || OHNE;
    if (!kvMap.has(kv)) kvMap.set(kv, { rows: [] });

    const studentName = a.studentName || (student ? `${student.firstName} ${student.lastName}` : "–");
    const cls = student ? (classes.find(c => c.id === student.classId)?.name || student.className || "–") : "–";
    const pInfo = a.practiceId ? practices.find(p => p.id === a.practiceId) : undefined;
    const practiceName = a.isFreeInput ? (a.freeInputText || "Freie Eingabe") : (a.practiceName || pInfo?.name || "–");
    const dept = departments.find(d => d.id === a.department);
    const from = weekStartDate(period.startDate, a.startWeek);
    const to = weekStartDate(period.startDate, a.startWeek + a.durationWeeks);
    to.setDate(to.getDate() - 1);

    const row = [
      studentName, cls, practiceName, dept?.nameDe || a.department, formatDE(from), formatDE(to), `${a.durationWeeks} Wo.`,
      a.internalNote || "", a.studentNote || "",
    ];
    if (showAbrechnung) row.push(abgerechnetLabel(a));
    kvMap.get(kv)!.rows.push(row);
  }

  // Gruppen alphabetisch, "Ohne Kreisverband" ans Ende; die Zeilen je Gruppe stehen schon
  // nach Nachname, Vorname (assignmentsByStudent), nicht nach dem Namenstext "Vorname Nachname"
  const sortedKv = [...kvMap.entries()].sort((a, b) => {
    if (a[0] === OHNE) return 1;
    if (b[0] === OHNE) return -1;
    return a[0].localeCompare(b[0], "de");
  });

  if (fmt === "xlsx") {
    const header = ["Kreisverband", "Schüler", "Klasse", "Praktikumsstelle", "Fachbereich", "Von", "Bis", "Dauer (Wo.)", "Interner Kommentar", "Kommentar für Schüler"];
    if (showAbrechnung) header.push("Abgerechnet");
    const xlsRows: string[][] = [header];
    let firstGroup = true;
    for (const [kv, { rows }] of sortedKv) {
      if (!firstGroup) xlsRows.push([]); // Leerzeile zwischen Kreisverbänden
      for (const row of rows) {
        xlsRows.push([kv, ...row]);
      }
      firstGroup = false;
    }
    const ws = XLSX.utils.aoa_to_sheet(xlsRows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Nach Kreisverband");
    XLSX.writeFile(wb, filename);

  } else {
    const logo = await loadLogo();
    const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    let first = true;

    for (const [kv, { rows }] of sortedKv) {
      if (!first) doc.addPage();
      first = false;

      const subtitle = [`Zeitraum: ${period.name}`, `Stand: ${new Date().toLocaleDateString("de-DE")}`].join("   ·   ");
      const y = addLogoAndHeader(doc, logo, kv, subtitle);

      if (rows.length === 0) {
        doc.setFontSize(9); doc.setTextColor(150, 150, 150);
        doc.text("Keine Zuweisungen in diesem Zeitraum.", 14, y + 6);
        continue;
      }

      autoTable(doc, {
        startY: y,
        head: [["Schüler", "Klasse", "Praktikumsstelle", "Fachbereich", "Von", "Bis", "Dauer"]],
        body: rows.map(r => r.slice(0, 7)),
        styles: { fontSize: 9, cellPadding: 3 },
        headStyles: { fillColor: [37, 99, 235] as [number, number, number], textColor: 255, fontStyle: "bold" },
        alternateRowStyles: { fillColor: [245, 247, 255] as [number, number, number] },
        margin: { left: 14, right: 14 },
      });
    }
    doc.save(filename);
  }
}

// ── 5. Fortschritts-Matrix ────────────────────────────────────────────────────

export interface ProgressExportCell { completed: number; planned: number; target: number; }

export interface ProgressExportData {
  /** Name der Fachbereichszuordnung; leer, wenn die Schule nur eine hat. */
  groupLabel: string;
  columns: { id: string; nameDe: string }[];
  rows: { studentName: string; className: string; cells: ProgressExportCell[]; total: ProgressExportCell }[];
  /** Spaltensummen je Fachbereich, in derselben Reihenfolge wie columns. */
  sums: ProgressExportCell[];
  grandTotal: ProgressExportCell;
}

export async function exportFortschritt(fmt: "xlsx" | "pdf", data: ProgressExportData): Promise<void> {
  const { groupLabel, columns, rows, sums, grandTotal } = data;
  const filename = safeFilename(`Fortschritt${groupLabel ? `_${groupLabel}` : ""}`, fmt);
  const subHead = ["Abs.", "Gepl.", "Soll"];

  if (fmt === "xlsx") {
    const head1: any[] = ["Schüler", "Klasse"];
    const head2: any[] = ["", ""];
    columns.forEach(c => { head1.push(c.nameDe, "", ""); head2.push(...subHead); });
    head1.push("Gesamt", "", ""); head2.push(...subHead);

    const body = rows.map(r => {
      const row: any[] = [r.studentName, r.className];
      r.cells.forEach(c => row.push(c.completed, c.planned, c.target));
      row.push(r.total.completed, r.total.planned, r.total.target);
      return row;
    });
    const sumRow: any[] = ["Summe", ""];
    sums.forEach(c => sumRow.push(c.completed, c.planned, c.target));
    sumRow.push(grandTotal.completed, grandTotal.planned, grandTotal.target);

    // Fachbereichs- und Gesamtueberschrift ueber ihre drei Unterspalten ziehen,
    // die beiden Stammdatenspalten ueber beide Kopfzeilen.
    const merges: XLSX.Range[] = [
      { s: { r: 0, c: 0 }, e: { r: 1, c: 0 } },
      { s: { r: 0, c: 1 }, e: { r: 1, c: 1 } },
    ];
    for (let i = 0; i <= columns.length; i++) {
      const c = 2 + i * 3;
      merges.push({ s: { r: 0, c }, e: { r: 0, c: c + 2 } });
    }

    const ws = XLSX.utils.aoa_to_sheet([head1, head2, ...body, sumRow]);
    ws["!merges"] = merges;
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Fortschritt");
    XLSX.writeFile(wb, filename);

  } else {
    const logo = await loadLogo();
    const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
    const subtitle = `${groupLabel ? `${groupLabel}   ·   ` : ""}Stand: ${new Date().toLocaleDateString("de-DE")}`;
    const y = addLogoAndHeader(doc, logo, "Stundenfortschritt", subtitle);

    // Zeile 2 enthaelt die beiden Stammdatenspalten nicht, sie sind per rowSpan abgedeckt.
    const head = [
      [
        { content: "Schüler", rowSpan: 2 },
        { content: "Klasse", rowSpan: 2 },
        ...columns.map(c => ({ content: c.nameDe, colSpan: 3, styles: { halign: "center" } })),
        { content: "Gesamt", colSpan: 3, styles: { halign: "center" } },
      ],
      [...columns.flatMap(() => subHead), ...subHead],
    ];
    const body = rows.map(r => [
      r.studentName, r.className,
      ...r.cells.flatMap(c => [c.completed, c.planned, c.target]),
      r.total.completed, r.total.planned, r.total.target,
    ]);
    const foot = [[
      "Summe", "",
      ...sums.flatMap(c => [c.completed, c.planned, c.target]),
      grandTotal.completed, grandTotal.planned, grandTotal.target,
    ]];

    autoTable(doc, {
      startY: y,
      head: head as any,
      body,
      foot: foot as any,
      styles: { fontSize: 7, cellPadding: 1.5, halign: "right" },
      headStyles: { fillColor: [37, 99, 235] as [number, number, number], textColor: 255, fontStyle: "bold", halign: "center" },
      footStyles: { fillColor: [243, 244, 246] as [number, number, number], textColor: 30, fontStyle: "bold" },
      columnStyles: { 0: { cellWidth: 34, halign: "left", fontStyle: "bold" }, 1: { cellWidth: 14, halign: "left" } },
      margin: { left: 10, right: 10 },
    });
    doc.save(filename);
  }
}

// ── Stundenplan-Export ────────────────────────────────────────────────────────

export interface TimetableExportData {
  className: string;
  from: string;
  to: string;
  slots: { id: number; label: string; startTime: string; endTime: string }[];
  lessons: {
    lessonDate: string;
    startTime: string;
    endTime: string;
    subjectName: string;
    subjectShortName: string | null;
    lecturerName: string | null;
    /** Wird nur vom Dozentenplan gebraucht, dort steht die Klasse in der Zelle. */
    className?: string;
    room: string | null;
    note: string | null;
    isPreliminary: boolean;
    /** Seit V53. Entfall und Vertretung gehoeren auf den ausgehaengten Plan. */
    status?: string;
    substituteLecturerName?: string | null;
    substituteRoom?: string | null;
  }[];
}

const EXPORT_WEEKDAYS = ["Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag", "Sonntag"];

function isoToDate(iso: string): Date {
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  return new Date(y, m - 1, d);
}

function isoOf(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * Beschriftung der Status-Spalte. Entfall und Vertretung wiegen schwerer als
 * "vorlaeufig" und stehen deshalb vorn; beides zugleich ist moeglich.
 */
function lessonStatusLabel(l: { status?: string; isPreliminary?: boolean }): string {
  const parts: string[] = [];
  if (l.status === "entfall") parts.push("Entfällt");
  else if (l.status === "vertretung") parts.push("Vertretung");
  if (l.isPreliminary) parts.push("Vorläufig");
  return parts.join(", ");
}

/**
 * Stundenplan einer Klasse.
 *
 * Excel wird eine flache Liste: so laesst sie sich sortieren und filtern, unabhaengig
 * davon, wie lang der Zeitraum ist. Das PDF bildet dagegen je Woche das gewohnte Raster
 * ab, weil es zum Aushaengen gedacht ist.
 */
export async function exportTimetable(fmt: "xlsx" | "pdf", data: TimetableExportData): Promise<void> {
  const { className, from, to, slots, lessons } = data;
  const rangeLabel = `${formatDE(isoToDate(from))} bis ${formatDE(isoToDate(to))}`;
  const filename = safeFilename(`Stundenplan_${className}_${from}_${to}`, fmt);

  const sorted = [...lessons].sort((a, b) =>
    a.lessonDate === b.lessonDate ? a.startTime.localeCompare(b.startTime) : a.lessonDate.localeCompare(b.lessonDate));

  if (fmt === "xlsx") {
    const rows = [
      ["Datum", "Wochentag", "Von", "Bis", "Fach", "Dozent", "Raum", "Notiz", "Status"],
      ...sorted.map(l => {
        const d = isoToDate(l.lessonDate);
        return [
          formatDE(d),
          EXPORT_WEEKDAYS[(d.getDay() + 6) % 7],
          l.startTime, l.endTime,
          l.subjectName,
          l.substituteLecturerName || l.lecturerName || "",
          l.substituteRoom || l.room || "",
          l.note || "",
          lessonStatusLabel(l),
        ];
      }),
    ];
    const ws = XLSX.utils.aoa_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Stundenplan");
    XLSX.writeFile(wb, filename);
    return;
  }

  const logo = await loadLogo();
  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  const y = addLogoAndHeader(doc, logo, `Stundenplan ${className}`,
    `${rangeLabel}   ·   Stand: ${new Date().toLocaleDateString("de-DE")}`);

  renderWeekGrids(doc, y, {
    slots, lessons: sorted, from, to,
    // Der ausgehaengte Plan muss den Ausfall zeigen, sonst steht die Klasse davor und
    // haelt ihn fuer gueltig.
    cellText: l => [
      (l.status === "entfall" ? "ENTFÄLLT: " : "") + (l.subjectShortName || l.subjectName),
      [l.substituteLecturerName || l.lecturerName, l.substituteRoom || l.room].filter(Boolean).join(" · "),
    ]
      .filter(Boolean).join("\n"),
  });

  doc.save(filename);
}

/**
 * Je Woche ein Raster in das PDF. Wochen ohne Unterricht werden uebersprungen, sonst
 * bestuende ein langer Zeitraum groesstenteils aus leeren Tabellen.
 *
 * Was in einer Zelle steht, entscheidet der Aufrufer: im Klassenplan interessiert der
 * Dozent, im Dozentenplan die Klasse.
 */
function renderWeekGrids(
  doc: jsPDF,
  startY: number,
  opts: {
    slots: TimetableExportData["slots"];
    lessons: TimetableExportData["lessons"];
    from: string;
    to: string;
    cellText: (l: TimetableExportData["lessons"][0]) => string;
  },
): number {
  const { slots, lessons, from, to, cellText } = opts;
  let y = startY;

  if (lessons.length === 0) {
    doc.setFontSize(10); doc.setFont("helvetica", "normal"); doc.setTextColor(100, 100, 100);
    doc.text("In diesem Zeitraum ist kein Unterricht geplant.", 14, y + 4);
    return y + 10;
  }

  const monday = isoToDate(from);
  monday.setDate(monday.getDate() + (1 - (monday.getDay() === 0 ? 7 : monday.getDay())));
  const last = isoToDate(to);

  let first = true;
  for (let week = new Date(monday); week <= last; week.setDate(week.getDate() + 7)) {
    const days = Array.from({ length: 5 }, (_, i) => {
      const d = new Date(week); d.setDate(d.getDate() + i); return d;
    });
    const inWeek = lessons.filter(l => days.some(d => isoOf(d) === l.lessonDate));
    if (inWeek.length === 0) continue;

    if (!first) { doc.addPage(); y = 14; }
    first = false;

    doc.setFontSize(11); doc.setFont("helvetica", "bold"); doc.setTextColor(30, 30, 30);
    doc.text(`${formatDE(days[0])} bis ${formatDE(days[4])}`, 14, y);
    y += 5;

    const body = slots.map(slot => {
      const cells = days.map(d => {
        const iso = isoOf(d);
        return inWeek
          .filter(l => l.lessonDate === iso && l.startTime < slot.endTime && slot.startTime < l.endTime)
          .map(cellText)
          .join("\n");
      });
      return [`${slot.label}\n${slot.startTime} bis ${slot.endTime}`, ...cells];
    });

    autoTable(doc, {
      startY: y,
      head: [["", ...days.map(d => `${EXPORT_WEEKDAYS[(d.getDay() + 6) % 7]}\n${formatDE(d)}`)]],
      body,
      styles: { fontSize: 8, cellPadding: 2, valign: "top" },
      headStyles: { fillColor: [37, 99, 235] as [number, number, number], textColor: 255, fontStyle: "bold" },
      columnStyles: { 0: { cellWidth: 30, fontStyle: "bold" } },
      margin: { left: 14, right: 14 },
    });
    y = (doc as any).lastAutoTable.finalY + 8;
  }
  return y;
}

// ── Übersicht aller Klassen ──

export interface OverviewExportData {
  from: string;
  to: string;
  slots: TimetableExportData["slots"];
  classes: { id: number; name: string }[];
  lessons: (TimetableExportData["lessons"][0] & {
    classId: number;
    changeNote?: string | null;
    fachFertig?: boolean;
    ueberSoll?: boolean;
  })[];
}

type OverviewLesson = OverviewExportData["lessons"][0];

/** Fachzeile mit allem, was auf einem ausgehängten Plan nicht fehlen darf. */
function overviewSubject(l: OverviewLesson): string {
  const name = l.subjectShortName || l.subjectName;
  const tags: string[] = [];
  if (l.status === "vertretung") tags.push("Vertretung");
  if (l.fachFertig) tags.push("Fach fertig");
  if (l.ueberSoll) tags.push("über Soll");
  if (l.isPreliminary) tags.push("vorläufig");
  return (l.status === "entfall" ? "ENTFÄLLT: " : "") + name + (tags.length ? ` (${tags.join(", ")})` : "");
}

function overviewRemark(l: OverviewLesson): string {
  return [l.note, l.changeNote].filter(Boolean).join(" · ");
}

/** Wochen im Zeitraum, je Woche die Tage, die angezeigt werden (Wochenende nur mit Unterricht). */
function overviewWeeks(data: OverviewExportData): { days: Date[]; lessons: OverviewLesson[] }[] {
  const monday = isoToDate(data.from);
  monday.setDate(monday.getDate() + (1 - (monday.getDay() === 0 ? 7 : monday.getDay())));
  const last = isoToDate(data.to);
  const weeks: { days: Date[]; lessons: OverviewLesson[] }[] = [];
  for (const week = new Date(monday); week <= last; week.setDate(week.getDate() + 7)) {
    const all = Array.from({ length: 7 }, (_, i) => { const d = new Date(week); d.setDate(d.getDate() + i); return d; });
    const inWeek = data.lessons.filter(l => all.some(d => isoOf(d) === l.lessonDate)
      && l.lessonDate >= data.from && l.lessonDate <= data.to);
    if (inWeek.length === 0) continue;
    const weekend = inWeek.some(l => { const wd = isoToDate(l.lessonDate).getDay(); return wd === 0 || wd === 6; });
    weeks.push({ days: weekend ? all : all.slice(0, 5), lessons: inWeek });
  }
  return weeks;
}

/** Termine einer Klasse an einem Tag, die den Block berühren. */
function overviewCell(lessons: OverviewLesson[], classId: number, iso: string, slot: TimetableExportData["slots"][0]): OverviewLesson[] {
  return lessons
    .filter(l => +l.classId === +classId && l.lessonDate === iso && l.startTime < slot.endTime && slot.startTime < l.endTime)
    .sort((a, b) => a.startTime.localeCompare(b.startTime));
}

/**
 * Wochenplan aller gewählten Klassen nebeneinander, wie in der Ansicht „Alle Klassen".
 *
 * Excel im Aufbau der Tabellen, mit denen Schulen ihre Woche bisher planen: Tag und Block
 * als Zeilen, je Klasse vier Spalten (Fach, Dozent, Bemerkung, Raum), je Woche ein Block
 * untereinander. Im PDF steht je Klasse eine Spalte mit allem in der Zelle, sonst wären
 * schon drei Klassen quer auf A4 nicht mehr lesbar.
 */
export async function exportOverview(fmt: "xlsx" | "pdf", data: OverviewExportData): Promise<void> {
  const { from, to, slots, classes } = data;
  const filename = safeFilename(`Stundenplan_Uebersicht_${from}_${to}`, fmt);
  const weeks = overviewWeeks(data);
  const weekTitle = (days: Date[]) => {
    const { week, year } = isoWeek(days[0]);
    return `KW ${week}/${year} · ${formatDE(days[0])} bis ${formatDE(days[days.length - 1])}`;
  };

  if (fmt === "xlsx") {
    const rows: (string | number)[][] = [];
    const merges: XLSX.Range[] = [];
    const width = 3 + classes.length * 4;
    if (weeks.length === 0) rows.push(["In diesem Zeitraum ist kein Unterricht geplant."]);

    for (const { days, lessons } of weeks) {
      if (rows.length > 0) rows.push([]);
      rows.push([weekTitle(days)]);
      merges.push({ s: { r: rows.length - 1, c: 0 }, e: { r: rows.length - 1, c: width - 1 } });

      const headRow = rows.length;
      rows.push(["Tag", "Datum", "Block", ...classes.flatMap(c => [c.name, "", "", ""])]);
      classes.forEach((_, i) => merges.push({ s: { r: headRow, c: 3 + i * 4 }, e: { r: headRow, c: 3 + i * 4 + 3 } }));
      rows.push(["", "", "", ...classes.flatMap(() => ["Fach", "Dozent", "Bemerkung", "Raum"])]);

      for (const day of days) {
        const iso = isoOf(day);
        const dayRow = rows.length;
        slots.forEach((slot, index) => {
          const cells = classes.flatMap(c => {
            const here = overviewCell(lessons, c.id, iso, slot);
            return [
              here.map(overviewSubject).join("\n"),
              here.map(l => l.substituteLecturerName || l.lecturerName || "").join("\n"),
              here.map(overviewRemark).join("\n"),
              here.map(l => l.substituteRoom || l.room || "").join("\n"),
            ];
          });
          rows.push([
            index === 0 ? EXPORT_WEEKDAYS[(day.getDay() + 6) % 7] : "",
            index === 0 ? formatDE(day) : "",
            `${slot.label} (${slot.startTime} bis ${slot.endTime})`,
            ...cells,
          ]);
        });
        if (slots.length > 1) {
          merges.push({ s: { r: dayRow, c: 0 }, e: { r: dayRow + slots.length - 1, c: 0 } });
          merges.push({ s: { r: dayRow, c: 1 }, e: { r: dayRow + slots.length - 1, c: 1 } });
        }
      }
    }

    const ws = XLSX.utils.aoa_to_sheet(rows);
    if (merges.length) ws["!merges"] = merges;
    ws["!cols"] = [{ wch: 11 }, { wch: 11 }, { wch: 22 }, ...classes.flatMap(() => [{ wch: 22 }, { wch: 16 }, { wch: 20 }, { wch: 9 }])];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Übersicht");
    XLSX.writeFile(wb, filename);
    return;
  }

  const logo = await loadLogo();
  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  let y = addLogoAndHeader(doc, logo, "Stundenplan aller Klassen",
    `${formatDE(isoToDate(from))} bis ${formatDE(isoToDate(to))}   ·   Stand: ${new Date().toLocaleDateString("de-DE")}`);

  if (weeks.length === 0) {
    doc.setFontSize(10); doc.setFont("helvetica", "normal"); doc.setTextColor(100, 100, 100);
    doc.text("In diesem Zeitraum ist kein Unterricht geplant.", 14, y + 4);
  }

  // Je mehr Klassen, desto kleiner die Schrift; ab acht Klassen bleibt es bei 6 pt.
  const fontSize = classes.length <= 3 ? 8 : classes.length <= 5 ? 7 : 6;
  weeks.forEach(({ days, lessons }, w) => {
    if (w > 0) { doc.addPage(); y = 14; }
    doc.setFontSize(11); doc.setFont("helvetica", "bold"); doc.setTextColor(30, 30, 30);
    doc.text(weekTitle(days), 14, y);
    y += 5;

    const body: any[] = [];
    for (const day of days) {
      const iso = isoOf(day);
      slots.forEach((slot, index) => {
        const row: any[] = [];
        if (index === 0) {
          row.push({ content: `${EXPORT_WEEKDAYS[(day.getDay() + 6) % 7]}\n${formatDE(day)}`, rowSpan: slots.length,
            styles: { fontStyle: "bold", fillColor: [243, 244, 246] } });
        }
        row.push(`${slot.label}\n${slot.startTime} bis ${slot.endTime}`);
        for (const c of classes) {
          row.push(overviewCell(lessons, c.id, iso, slot).map(l => [
            overviewSubject(l),
            [l.substituteLecturerName || l.lecturerName, l.substituteRoom || l.room].filter(Boolean).join(" · "),
            overviewRemark(l),
          ].filter(Boolean).join("\n")).join("\n\n"));
        }
        body.push(row);
      });
    }

    autoTable(doc, {
      startY: y,
      head: [["Tag", "Block", ...classes.map(c => c.name)]],
      body,
      styles: { fontSize, cellPadding: 1.5, valign: "top", lineColor: [209, 213, 219], lineWidth: 0.1 },
      headStyles: { fillColor: [37, 99, 235] as [number, number, number], textColor: 255, fontStyle: "bold" },
      columnStyles: { 0: { cellWidth: 22 }, 1: { cellWidth: 24 } },
      margin: { left: 14, right: 14 },
    });
    y = (doc as any).lastAutoTable.finalY + 8;
  });

  doc.save(filename);
}

// ── Dozentenplan ──

export interface LecturerTimetableExportData {
  from: string;
  to: string;
  slots: TimetableExportData["slots"];
  /** Ein Eintrag je Dozent; bei "alle Dozenten" entsprechend mehrere. */
  lecturers: { name: string; lessons: TimetableExportData["lessons"] }[];
}

/**
 * Stundenplan aus Sicht der Dozenten. Gleiche Aufteilung wie beim Klassenplan: Excel
 * wird eine sortier- und filterbare Liste, das PDF bildet je Woche das Raster ab, hier
 * mit der Klasse statt dem Dozenten in der Zelle.
 */
export async function exportLecturerTimetable(
  fmt: "xlsx" | "pdf", data: LecturerTimetableExportData,
): Promise<void> {
  const { from, to, slots, lecturers } = data;
  const rangeLabel = `${formatDE(isoToDate(from))} bis ${formatDE(isoToDate(to))}`;
  const single = lecturers.length === 1 ? lecturers[0].name : "Alle Dozenten";
  const filename = safeFilename(`Dozentenplan_${single}_${from}_${to}`, fmt);

  const sortLessons = (list: TimetableExportData["lessons"]) => [...list].sort((a, b) =>
    a.lessonDate === b.lessonDate ? a.startTime.localeCompare(b.startTime) : a.lessonDate.localeCompare(b.lessonDate));

  if (fmt === "xlsx") {
    const rows: string[][] = [["Dozent", "Datum", "Wochentag", "Von", "Bis", "Fach", "Klasse", "Raum", "Notiz", "Status"]];
    for (const lecturer of lecturers) {
      for (const l of sortLessons(lecturer.lessons)) {
        const d = isoToDate(l.lessonDate);
        rows.push([
          lecturer.name,
          formatDE(d),
          EXPORT_WEEKDAYS[(d.getDay() + 6) % 7],
          l.startTime, l.endTime,
          l.subjectName,
          l.className || "",
          l.substituteRoom || l.room || "",
          l.note || "",
          lessonStatusLabel(l),
        ]);
      }
    }
    const ws = XLSX.utils.aoa_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Dozentenplan");
    XLSX.writeFile(wb, filename);
    return;
  }

  const logo = await loadLogo();
  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });

  lecturers.forEach((lecturer, index) => {
    // Jeder Dozent beginnt auf einer eigenen Seite: der Plan wird einzeln ausgeteilt.
    if (index > 0) doc.addPage();
    const y = addLogoAndHeader(doc, logo, `Stundenplan ${lecturer.name}`,
      `${rangeLabel}   ·   Stand: ${new Date().toLocaleDateString("de-DE")}`);
    renderWeekGrids(doc, y, {
      slots, lessons: sortLessons(lecturer.lessons), from, to,
      cellText: l => [
      (l.status === "entfall" ? "ENTFÄLLT: " : "") + (l.subjectShortName || l.subjectName),
      [l.className, l.substituteRoom || l.room].filter(Boolean).join(" · "),
    ]
        .filter(Boolean).join("\n"),
    });
  });

  doc.save(filename);
}

// ── Auswertung: Unterrichtsstunden ────────────────────────────────────────────

export interface LessonHoursExportRow {
  label: string;
  /** Werte je Monat, in derselben Reihenfolge wie monate. */
  months: number[];
  /** Zeilensumme ueber die Monate, fuer die Teiltabellen im PDF. */
  ist: number;
  /** Die Werte des Gesamtblocks, in derselben Reihenfolge wie totalHeads. */
  totals: (string | number)[];
  /** Fächergruppe: Summe ihrer Fächer, zählt in keiner Spaltensumme und wird hervorgehoben. */
  group?: boolean;
}

export interface LessonHoursExportData {
  /** Klassenname, bei mehreren Klassen ein Sammelbegriff. */
  title: string;
  von: string;
  bis: string;
  /** "Std." oder "UE". */
  unitLabel: string;
  /** "Fach" oder "Klasse". */
  nameLabel: string;
  /** Bereits beschriftete Monatsspalten ("Sep 24"). */
  monate: string[];
  /** Stufenbaender ueber den Monaten, span in Spalten. Leer = keine Baender. */
  bands: { label: string; span: number }[];
  /** Ueberschriften des Gesamtblocks rechts, etwa Ist, Soll, Rest. */
  totalHeads: string[];
  /** Die Summenzeile des Gesamtblocks, gleiche Laenge wie totalHeads. */
  totalFoot: (string | number)[];
  rows: LessonHoursExportRow[];
}

export async function exportUnterrichtsstunden(fmt: "xlsx" | "pdf", data: LessonHoursExportData): Promise<void> {
  const { title, von, bis, unitLabel, nameLabel, monate, bands, totalHeads, totalFoot, rows } = data;
  const filename = safeFilename(`Unterrichtsstunden_${title}`, fmt);
  const totalHead = totalHeads;
  // Gruppenzeilen fassen ihre Faecher nur zusammen; mitgezaehlt stuende alles doppelt.
  const monthSums = monate.map((_, i) => rows.reduce((sum, r) => sum + (r.group ? 0 : r.months[i]), 0));
  const num = (n: number) => Math.round(n * 10) / 10;

  const bodyRow = (r: LessonHoursExportRow): (string | number)[] => [
    r.label, ...r.months.map(num), ...r.totals,
  ];
  const sumRow: (string | number)[] = ["Summe", ...monthSums.map(num), ...totalFoot];

  if (fmt === "xlsx") {
    const sheet: any[][] = [];
    const merges: XLSX.Range[] = [];

    if (bands.length > 0) {
      // Bandzeile: die Beschriftung steht in der ersten Spalte des Bandes, die
      // uebrigen bleiben leer und werden darueber zusammengezogen.
      const head1: any[] = [""];
      let col = 1;
      for (const b of bands) {
        head1.push(b.label, ...Array(b.span - 1).fill(""));
        if (b.span > 1) merges.push({ s: { r: 0, c: col }, e: { r: 0, c: col + b.span - 1 } });
        col += b.span;
      }
      head1.push("Gesamt", ...Array(totalHead.length - 1).fill(""));
      if (totalHead.length > 1) merges.push({ s: { r: 0, c: col }, e: { r: 0, c: col + totalHead.length - 1 } });
      merges.push({ s: { r: 0, c: 0 }, e: { r: 1, c: 0 } });
      sheet.push(head1);
    }
    sheet.push([nameLabel, ...monate, ...totalHead]);
    rows.forEach(r => sheet.push(bodyRow(r)));
    sheet.push(sumRow);

    const ws = XLSX.utils.aoa_to_sheet(sheet);
    if (merges.length > 0) ws["!merges"] = merges;
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Unterrichtsstunden");
    XLSX.writeFile(wb, filename);
    return;
  }

  const logo = await loadLogo();
  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  const zeitraum = `${formatDE(new Date(von))} bis ${formatDE(new Date(bis))}`;
  const subtitle = `${title}   ·   ${zeitraum}   ·   Angaben in ${unitLabel}   ·   Stand: ${formatDE(new Date())}`;
  let y = addLogoAndHeader(doc, logo, "Unterrichtsstunden", subtitle);

  /**
   * Kennung auf jeder Folgeseite. Wer im PDF blaettert, sieht sonst nur noch
   * "2. Ausbildungsjahr" und weiss nicht mehr, wessen Auswertung er vor sich hat.
   * Auf Seite 1 steht dieselbe Zeile bereits unter dem Logo.
   *
   * Als didDrawPage, damit es auch fuer Seiten gilt, die autoTable selbst anlegt,
   * wenn eine Teiltabelle laenger wird als das Blatt.
   */
  const seitenkopf = () => {
    const seite = doc.getCurrentPageInfo?.()?.pageNumber ?? doc.getNumberOfPages();
    if (seite <= 1) return;
    doc.setFontSize(8); doc.setFont("helvetica", "normal"); doc.setTextColor(120, 120, 120);
    doc.text(subtitle, 14, 11);
  };

  const groupRowIdx = new Set(rows.map((r, i) => (r.group ? i : -1)).filter(i => i >= 0));
  const styles = {
    styles: { fontSize: 7, cellPadding: 1.5, halign: "right" as const },
    headStyles: { fillColor: [37, 99, 235] as [number, number, number], textColor: 255, fontStyle: "bold" as const, halign: "center" as const },
    footStyles: { fillColor: [243, 244, 246] as [number, number, number], textColor: 30, fontStyle: "bold" as const },
    columnStyles: { 0: { cellWidth: 38, halign: "left" as const, fontStyle: "bold" as const } },
    // top gilt fuer Folgeseiten und laesst der Kennung Platz.
    margin: { left: 10, right: 10, top: 18 },
    didDrawPage: seitenkopf,
    // Faechergruppen grau hinterlegt, wie in den Stundentafeln der Schulen.
    didParseCell: (hook: any) => {
      if (hook.section === "body" && groupRowIdx.has(hook.row.index)) {
        hook.cell.styles.fillColor = [229, 231, 235];
        hook.cell.styles.fontStyle = "bold";
      }
    },
  };

  // Ueber drei Jahre sind es 36 Monatsspalten; quer auf A4 waere das nicht mehr
  // lesbar. Deshalb je Ausbildungsjahr eine eigene Tabelle, sobald es eng wird.
  const proBand = bands.length > 1 && monate.length > 18;
  if (!proBand) {
    autoTable(doc, {
      ...styles, startY: y,
      head: [[nameLabel, ...monate, ...totalHead]],
      body: rows.map(bodyRow),
      foot: [sumRow],
    });
    doc.save(filename);
    return;
  }

  let from = 0;
  bands.forEach((band, i) => {
    const slice = { start: from, end: from + band.span };
    from += band.span;
    const teilMonate = monate.slice(slice.start, slice.end);
    const teilRows = rows.map(r => {
      const months = r.months.slice(slice.start, slice.end);
      return [r.label, ...months.map(num), num(months.reduce((a, b) => a + b, 0))];
    });
    const teilSumme = ["Summe",
      ...monthSums.slice(slice.start, slice.end).map(num),
      num(monthSums.slice(slice.start, slice.end).reduce((a, b) => a + b, 0))];

    if (i > 0) { doc.addPage(); y = 18; }
    doc.setFontSize(10); doc.setFont("helvetica", "bold"); doc.setTextColor(30, 30, 30);
    doc.text(band.label, 14, y);
    autoTable(doc, {
      ...styles, startY: y + 4,
      head: [[nameLabel, ...teilMonate, "Summe"]],
      body: teilRows,
      foot: [teilSumme],
    });
  });

  // Letzte Seite: der Gesamtblock ueber alle Baender, damit er nicht in den
  // Teiltabellen untergeht.
  doc.addPage();
  y = 18;
  doc.setFontSize(10); doc.setFont("helvetica", "bold"); doc.setTextColor(30, 30, 30);
  doc.text("Gesamt über den ganzen Zeitraum", 14, y);
  autoTable(doc, {
    ...styles, startY: y + 4,
    head: [[nameLabel, ...totalHead]],
    body: rows.map(r => [r.label, ...r.totals]),
    foot: [["Summe", ...totalFoot]],
  });
  doc.save(filename);
}

// ── Auswertungen: Wünsche pro Praktikumsstelle ──

export interface WishExportData {
  /** Bereits gefiltert, wie auf dem Bildschirm; Blöcke ohne Zeile fehlen. */
  stellen: WishReportBlock[];
  /** Aktive Filter als Text für den PDF-Kopf, leer ohne Filter. */
  filter: string;
}

/** Vermerk je Zeile, gleichlautend in Excel und PDF. */
function wishHinweis(e: WishReportEntry): string {
  return e.exact ? "" : "Zeitpunkt ungenau";
}

export async function exportWuensche(fmt: "xlsx" | "pdf", data: WishExportData): Promise<void> {
  const { stellen, filter } = data;
  const filename = safeFilename("Wuensche_pro_Praktikumsstelle", fmt);

  if (fmt === "xlsx") {
    const header = ["Praktikumsstelle", "Ort", "Fachbereich", "Rang", "Schüler", "Klasse", "Wunsch-Nr.", "Eingetragen am", "Hinweis"];
    const rows: (string | number)[][] = [header];
    stellen.forEach((b, i) => {
      if (i > 0) rows.push([]); // Leerzeile zwischen den Stellen
      for (const e of b.eintraege) {
        rows.push([
          b.practiceName, b.ort || "", b.departmentName,
          e.rank, e.studentName, e.className || "", e.priority,
          formatDateTime(e.createdAt), wishHinweis(e),
        ]);
      }
    });
    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws["!cols"] = [{ wch: 32 }, { wch: 18 }, { wch: 22 }, { wch: 6 }, { wch: 26 }, { wch: 12 }, { wch: 10 }, { wch: 21 }, { wch: 32 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Wünsche");
    XLSX.writeFile(wb, filename);
    return;
  }

  const logo = await loadLogo();
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const subtitle = [filter, `Stand: ${formatDE(new Date())}`].filter(Boolean).join("   ·   ");
  let y = addLogoAndHeader(doc, logo, "Wünsche pro Praktikumsstelle", subtitle) + 2;
  const pageH = doc.internal.pageSize.getHeight();

  for (const b of stellen) {
    // Überschrift nicht allein unten auf der Seite stehen lassen.
    if (y > pageH - 40) { doc.addPage(); y = 18; }
    doc.setFontSize(10); doc.setFont("helvetica", "bold"); doc.setTextColor(30, 30, 30);
    doc.text(`${b.practiceName}  ·  ${b.departmentName}`, 14, y);
    if (b.ort) {
      doc.setFontSize(8); doc.setFont("helvetica", "normal"); doc.setTextColor(110, 110, 110);
      doc.text(b.ort, 14, y + 4.5);
      y += 4.5;
    }
    autoTable(doc, {
      startY: y + 2,
      head: [["Rang", "Schüler", "Klasse", "Wunsch", "Eingetragen am", "Hinweis"]],
      body: b.eintraege.map(e => [
        String(e.rank), e.studentName, e.className || "",
        `Nr. ${e.priority}`, formatDateTime(e.createdAt), wishHinweis(e),
      ]),
      styles: { fontSize: 8, cellPadding: 1.8 },
      headStyles: { fillColor: [37, 99, 235] as [number, number, number], textColor: 255, fontStyle: "bold" },
      columnStyles: { 0: { cellWidth: 12, halign: "right" as const }, 3: { cellWidth: 16 }, 4: { cellWidth: 34 } },
      margin: { left: 14, right: 14, top: 18 },
    });
    y = (doc as any).lastAutoTable.finalY + 8;
  }
  doc.save(filename);
}
